import React from 'react';
import { Grid, GridItem, Spacer } from 'ech-components/src/helpers';
import Custom from '@components/Custom/Custom';
import CustomInline from '@components/Custom/CustomInline';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  P2,
  LEAD1,
} from 'ech-components/src/primitives';
import classNames from 'classnames';

interface Target {
  __typename: string;
  image?: { fluid?: string };
  fluid?: string;
}

interface Options {
  renderMark: any;
  renderNode: any;
}

const Bold = ({ children }: any) => <span className="bold">{children}</span>;
const Sup = ({ children }: { children: string }) => <sup>{children}</sup>;
const Sub = ({ children }: { children: string }) => <sub>{children}</sub>;
const Text = ({ children }: any) => <P2>{children}</P2>;
const TextLead = ({ children }: any) => <LEAD1>{children}</LEAD1>;

Bold.displayName = 'BoldComponent';
Text.displayName = 'TextComponent';
TextLead.displayName = 'TextLeadComponent';

export const options: Options = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
    [MARKS.SUPERSCRIPT]: (text: string) => <Sup>{text}</Sup>,
    [MARKS.SUBSCRIPT]: (text: string) => <Sub>{text}</Sub>,
  },
  renderNode: {
    [BLOCKS.HEADING_1]: (_node: any, children: any) => <H1>{children}</H1>,
    [BLOCKS.HEADING_2]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="hecto" />}
        <H2>{children}</H2>
        {!_node.lastNode && <Spacer space="deci" />}
      </>
    ),
    [BLOCKS.HEADING_3]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="mega" />}
        <H3>{children}</H3>
        {!_node.lastNode && <Spacer space="deci" />}
      </>
    ),
    [BLOCKS.HEADING_4]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="mega" />}
        <H4>{children}</H4>
        {!_node.lastNode && <Spacer space="deci" />}
      </>
    ),
    [BLOCKS.HEADING_5]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="mega" />}
        <H5>{children}</H5>
        {!_node.lastNode && <Spacer space="deci" />}
      </>
    ),
    [BLOCKS.HEADING_6]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="mega" />}
        <H6>{children}</H6>
        {!_node.lastNode && <Spacer space="deci" />}
      </>
    ),
    [BLOCKS.PARAGRAPH]: (
      node: { data: { target: Target; fluid: string } },
      _children: any,
    ) => {
      return <Text>{_children}</Text>;
    },
    [BLOCKS.UL_LIST]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="gamma" />}
        <ul>{children}</ul>
        {!_node.lastNode && <Spacer space="gamma" />}
      </>
    ),
    [BLOCKS.OL_LIST]: (_node: any, children: any) => (
      <>
        {!_node.firstNode && <Spacer space="gamma" />}
        <ol>{children}</ol>
        {!_node.lastNode && <Spacer space="gamma" />}
      </>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (
      node: { data: { target: Target; fluid: string } },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _children: any,
    ) => {
      if (!node.data.target) {
        return false;
      }
      const nodeType = node.data.target.__typename;
      const { target } = node.data;

      return <Custom nodeType={nodeType} data={target} />;
    },
    [BLOCKS.EMBEDDED_ENTRY]: (
      node: { data: { target: Target; fluid: string } },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _children: any,
    ) => {
      if (!node.data.target) {
        return false;
      }
      const nodeType = node.data.target.__typename;
      const { target } = node.data;

      return <Custom nodeType={nodeType} data={target} />;
    },
    [INLINES.EMBEDDED_ENTRY]: (
      node: { data: { target: { __typename: any } } },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _children: any,
    ) => {
      if (!node.data.target) {
        return false;
      }
      const nodeType = node.data.target.__typename;
      const { target } = node.data;

      return <CustomInline nodeType={nodeType} data={target} />;
    },
    [INLINES.ENTRY_HYPERLINK]: (
      node: {
        data: {
          target: {
            __typename: string;
            hyperlinkText: string;
          };
        };
      },
      _children: [string] | [object],
    ) => {
      if (!node.data.target) {
        return false;
      }

      const target = { ...node.data.target, hyperlinkText: _children[0] };
      const nodeType = target.__typename;

      return <CustomInline nodeType={nodeType} data={target} />;
    },
    [INLINES.ASSET_HYPERLINK]: (
      node: {
        data: {
          target: {
            __typename: string;
            hyperlinkText: string;
          };
        };
      },
      _children: [string] | [object],
    ) => {
      if (!node.data.target) {
        return false;
      }

      const target = { ...node.data.target, hyperlinkText: _children[0] };
      const nodeType = target.__typename;

      return <CustomInline nodeType={nodeType} data={target} />;
    },
    grid: (
      node: {
        data: {
          target: { type: any; grid: { col: any; size: any; push: any } };
        };
        multiItem: boolean;
        firstRow: boolean;
        lastRow: boolean;
        crossRow: boolean;
        nested: boolean;
        content: {
          nodeType: string;
        }[];
      },
      children: React.ReactNode,
    ) => {
      const gridCol = node.data.target.grid.col;
      const gridSize = node.data.target.grid.size;
      const gridPush = node.data.target.grid.push;

      return (
        <>
          {node.firstRow &&
            !node.nested &&
            node.content[0].nodeType === 'embedded-entry-block' && (
              <Spacer space="hecto" />
            )}
          <Grid
            columns={gridSize}
            className={classNames('m-richtext', {
              'm-richtext--multi-item': node.multiItem,
              'm-richtext--nested': node.nested,
            })}
          >
            <GridItem size={gridCol} push={gridPush}>
              {children}
            </GridItem>
          </Grid>
          {node.crossRow ? (
            <Spacer space="hecto" />
          ) : !node.lastRow ? (
            <Spacer space={node.nested ? 'hecto' : 'peta'} />
          ) : null}
        </>
      );
    },
  },
};

export const optionsLead: Options = {
  renderMark: {
    [MARKS.BOLD]: (text: any) => <Bold>{text}</Bold>,
    [MARKS.SUPERSCRIPT]: (text: string) => <Sup>{text}</Sup>,
    [MARKS.SUBSCRIPT]: (text: string) => <Sub>{text}</Sub>,
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: any, children: any) => (
      <TextLead>{children}</TextLead>
    ),
    [BLOCKS.EMBEDDED_ASSET]: (
      node: { data: { target: Target } },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _children: any,
    ) => {
      const nodeType = node.data.target.__typename;
      const { target } = node.data;
      target.image = {};
      target.image.fluid = target.fluid;

      return <Custom nodeType={nodeType} data={target} />;
    },
  },
};
